import React, { useState } from 'react'
import { Accordion, Badge, Button, Col, Table } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { EventOccupation } from './EventOccupation'
import { EventCodeOccupation } from './EventCodeOccupation'
import { EventTimeSpan } from './EventTimeSpan'
import { EventAge } from './EventAge'
import { CustomAnswer, IEvent, IEventAttachment, Timespan } from '../../store/events/interfaces'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { textWithLineBreaks } from '../../functions/utils'
import { selectHasPaidEvents } from '../../store/events/selectors'
import { formatPrice } from '../../utils'

export const EventDetailList = (props: { event: IEvent }) => {
  const { event } = props
  const { data: program } = useSelector((state: RootState) => state.program)
  const hasPaidEvents = useSelector(selectHasPaidEvents)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const highlight = queryParams.get('highlight')

  const getRowClassName = (rowId: string) => {
    return highlight === rowId ? 'highlight-animation' : ''
  }

  const renderRegistrationDateRow = () => {
    const showFirstDate = event.first_registration_date && (!program.start_registration_date || new Date(event.first_registration_date) > new Date(program.start_registration_date))
    const showLastDate = event.last_registration_date && (!program.last_registration_date || new Date(event.last_registration_date) < new Date(program.last_registration_date))

    if (showFirstDate && showLastDate) {
      return (
        <tr id="registration-period" className={getRowClassName('registration-period')}>
          <th>Anmeldezeitraum</th>
          <td>
            {new Date(event.first_registration_date).toLocaleDateString('de-DE')} bis {new Date(event.last_registration_date).toLocaleDateString('de-DE')}
          </td>
        </tr>
      )
    } else if (showFirstDate) {
      return (
        <tr id="first-registration-date" className={getRowClassName('first-registration-date')}>
          <th>Anmeldestart</th>
          <td>
            {new Date(event.first_registration_date).toLocaleDateString('de-DE')}
          </td>
        </tr>
      )
    } else if (showLastDate) {
      return (
        <tr id="last-registration-date" className={getRowClassName('last-registration-date')}>
          <th>Anmeldeschluss</th>
          <td>
            {new Date(event.last_registration_date).toLocaleDateString('de-DE')}
          </td>
        </tr>
      )
    }
    return null
  }

  const [open, setOpen] = useState(false)
  const organizer = event.organizer
  let organizerHasSocialMedia = !!(
    organizer.twitter ||
    organizer.facebook ||
    organizer.snapchat ||
    organizer.instagram
  )
  let organizerDataAvailable = !!(
    organizer.website ||
    organizer.phone ||
    organizer.email ||
    organizer.description ||
    organizerHasSocialMedia
  )

  return (
    <>
      <Table bordered responsive>
        <tbody>
          {!organizerDataAvailable && (
            <tr id="organizer" className={getRowClassName('organizer')}>
              <th>Veranstalter</th>
              <td>{organizer.name}</td>
            </tr>
          )}
          <tr id="when" className={getRowClassName('when')}>
            <th>Wann</th>
            <td>
              {event.timespans ? (
                <>
                  {event.timespans && event.timespans?.length > 1 ? (
                    <>
                      <p>Veranstaltung findet an den jeweiligen Tagen statt:</p>
                      <ul>
                        {event.timespans?.map(
                          (timespan: Timespan, key: number) => (
                            <li key={`${event.event_id}-ts-${key}`}>
                              <EventTimeSpan timespan={timespan} />
                            </li>
                          )
                        )}
                      </ul>
                    </>
                  ) : (
                    <>
                      {event.timespans?.map(
                        (timespan: Timespan, key: number) => (
                          <span key={`${event.event_id}-ts-${key}`}>
                            <EventTimeSpan timespan={timespan} />
                          </span>
                        )
                      )}
                    </>
                  )}
                </>
              ) : (
                <>{event.duration_summary}</>
              )}
            </td>
          </tr>
          <tr id="meeting-point" className={getRowClassName('meeting-point')}>
            <th>Treffpunkt</th>
            {event.get_location_link ? (
              <td
                dangerouslySetInnerHTML={{ __html: event.get_location_link }}
              />
            ) : (
              <td>{textWithLineBreaks(event.meeting_point)}</td>
            )}
          </tr>
          {hasPaidEvents && (
            <tr id="costs" className={getRowClassName('costs')}>
              <th>Kosten</th>
              <td>
                {event.price_categories.length > 0 ? (
                  <>
                    <ul className="list-unstyled">
                      <li>{event.display_price}</li>
                      {event.price_categories.map((item, index) => (
                        <li key={index}>
                          {item.name}: {formatPrice(item.price)}
                        </li>
                      ))}
                    </ul>
                    {event.has_external_payment && (
                      <>
                        <Badge variant="warning" tabIndex={0}>
                          Achtung
                        </Badge>{' '}
                        <small className="text-muted">
                          Bezahlung wird direkt beim Veranstalter geregelt!
                        </small>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {event.price ? (
                      <>
                        {formatPrice(event.price)}
                        {event.has_external_payment && (
                          <>
                            <br />
                            <Badge variant="warning" tabIndex={0}>
                              Achtung
                            </Badge>{' '}
                            <small className="text-muted">
                              Bezahlung wird direkt beim Veranstalter
                              geregelt!
                            </small>
                          </>
                        )}
                      </>
                    ) : (
                      'Kostenlos'
                    )}
                  </>
                )}
              </td>
            </tr>
          )}
          <tr id="age-restriction" className={getRowClassName('age-restriction')}>
            <th>
              {program.requires_family_data
                ? 'Altersempfehlung'
                : 'Altersbeschränkung'}
            </th>
            <td>
              <EventAge
                event={event}
                isFamilyRequired={program.requires_family_data}
              />
            </td>
          </tr>
          {renderRegistrationDateRow()}
          {program.booking_visibility !== 'never' &&
          !program.hide_event_occupation &&
          !event.has_external_registration ? (
            <>
              <tr id="occupancy" className={getRowClassName('occupancy')}>
                <th>Belegung</th>
                <td>
                  <EventOccupation event={event} isModal={true} />
                </td>
              </tr>
            </>
          ) : (
            <></>
          )}
          {program.booking_visibility_codes !== 'never' ? (
            <>
              <tr id="code-occupancy" className={getRowClassName('code-occupancy')}>
                <th>Code Kategorien Belegung</th>
                <td>
                  <EventCodeOccupation event={event} />
                </td>
              </tr>
            </>
          ) : (
            <></>
          )}

          {event.tags.length > 0 && (
            <tr id="categories" className={getRowClassName('categories')}>
              <th>Kategorien</th>
              <td>
                {event.tags.map((tag: string, key) => (
                  <Badge
                    variant="success"
                    key={key}
                    tabIndex={0}
                    className={'mr-1'}
                  >
                    {tag}
                  </Badge>
                ))}
              </td>
            </tr>
          )}
          {event.excluded_events.length > 0 && (
            <tr id="excluded-events" className={getRowClassName('excluded-events')}>
              <th>Verhindert Anmeldungen in folgenden Veranstaltungen</th>
              <td>
                {event.excluded_events.length > 0 ? (
                  <ul>
                    {event.excluded_events.map((excluded: string, key) => (
                      <li key={key}>{excluded}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted">
                    Es wurden keine Veranstaltungen angegeben.
                  </span>
                )}
              </td>
            </tr>
          )}
          {event.name_public && (
            <tr id="contact" className={getRowClassName('contact')}>
              <th>Ansprechpartner</th>
              <td>
                {event.name_public}
                {event.phone_public && (
                  <>
                    <br />
                    <FontAwesomeIcon icon="mobile-alt" />{' '}
                    <a href={`tel:${event.phone_public}`}>
                      {event.phone_public}
                    </a>
                  </>
                )}
                {event.email_public && (
                  <>
                    <br />
                    <FontAwesomeIcon icon="envelope" />{' '}
                    <a href={`mailto:${event.email_public}`}>
                      {event.email_public}
                    </a>
                  </>
                )}
              </td>
            </tr>
          )}
          {event.attachments.length > 0 && (
            <tr id="attachments" className={getRowClassName('attachments')}>
              <th>Anhänge</th>
              <td>
                {event.attachments.length > 0 ? (
                  <ul>
                    {event.attachments.map(
                      (attachment: IEventAttachment, key) => (
                        <li key={key}>
                          <a
                            className="noscript"
                            href={attachment.url}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {attachment.title}
                          </a>
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <span className="text-muted">
                    Es wurden zu dieser Veranstaltung keine Anhänge angegeben.
                  </span>
                )}
              </td>
            </tr>
          )}
          {event.has_external_registration && (
            <tr id="external-registration" className={getRowClassName('external-registration')}>
              <th>Externe Anmeldung</th>
              <td dangerouslySetInnerHTML={{__html: event.external_registration_description_html ? event.external_registration_description_html : "Für die Anmeldung zu dieser Veranstaltung gelten besondere Bedingungen. Nähere Hinweise entnehmen Sie bitte der obenstehenden Beschreibung."}}>
              </td>
            </tr>
          )}
        </tbody>
        {!!event.custom_fields && (
          <tfoot>
            {event.custom_fields.map((field: CustomAnswer, key: number) => {
              if (field.question_scope !== 'registration_event_specific') {
                return (
                  <tr key={key} id={`custom-field-${key}`} className={getRowClassName(`custom-field-${key}`)}>
                    <th>
                      {field.question_label}
                      <small className="text-muted">
                        {' '}
                        <br />
                        {field.question_description}
                      </small>
                    </th>
                    <td>{field.answer_text}</td>
                  </tr>
                )
              } else {
                //TODO: this is weird?
                return undefined
              }
            })}
          </tfoot>
        )}
      </Table>
      {organizerDataAvailable && (
        <Accordion>
          <Accordion.Toggle
            as={Button}
            variant="button"
            eventKey="0"
            onClick={() => setOpen(!open)}
            className="btn-info"
            aria-expanded={open}
          >
            <>
              Informationen über den Veranstalter{' '}
              <span className={'font-weight-bold'}>{organizer.name}</span>{' '}
              <FontAwesomeIcon icon={open ? 'caret-down' : 'caret-right'} />
            </>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <Col md={12} className="px-0 pt-2">
              <Table bordered responsive>
                <tbody>
                  {organizer.website && (
                    <tr id="organizer-website" className={getRowClassName('organizer-website')}>
                      <th>Webseite</th>
                      <td>
                        <a
                          className="dark-primary-text"
                          href={`${organizer.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {organizer.website}
                        </a>
                      </td>
                    </tr>
                  )}
                  {organizerHasSocialMedia && (
                    <tr id="organizer-social-media" className={getRowClassName('organizer-social-media')}>
                      <th>Social Media</th>
                      <Table className="mb-0">
                        <tbody>
                          <tr id="organizer-social-media-channels" className={getRowClassName('organizer-social-media-channels')}>
                            {organizer.facebook && (
                              <td>
                                <a
                                  href={`${organizer.facebook}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Facebook{' '}
                                  <FontAwesomeIcon icon={['fab', 'facebook']} />
                                </a>
                              </td>
                            )}
                            {organizer.instagram && (
                              <td>
                                <a
                                  href={`${organizer.instagram}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Instagram{' '}
                                  <FontAwesomeIcon
                                    icon={['fab', 'instagram']}
                                  />
                                </a>
                              </td>
                            )}
                            {organizer.twitter && (
                              <td>
                                <a
                                  href={`${organizer.twitter}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Twitter{' '}
                                  <FontAwesomeIcon icon={['fab', 'twitter']} />
                                </a>
                              </td>
                            )}
                            {organizer.snapchat && (
                              <td>
                                <a
                                  href={`${organizer.snapchat}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Snapchat{' '}
                                  <FontAwesomeIcon icon={['fab', 'snapchat']} />
                                </a>
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </Table>
                    </tr>
                  )}
                  {organizer.phone && (
                    <tr id="organizer-phone" className={getRowClassName('organizer-phone')}>
                      <th>Telefon</th>
                      <td>
                        <FontAwesomeIcon icon="mobile-alt" />{' '}
                        <a href={`tel:${organizer.phone}`}>{organizer.phone}</a>
                      </td>
                    </tr>
                  )}
                  {organizer.email && (
                    <tr id="organizer-email" className={getRowClassName('organizer-email')}>
                      <th>E-Mail</th>
                      <td>
                        <FontAwesomeIcon icon="envelope" />{' '}
                        <a href={`mailto:${organizer.email}`}>
                          {organizer.email}
                        </a>
                      </td>
                    </tr>
                  )}
                  {organizer.description && (
                    <tr id="organizer-description" className={getRowClassName('organizer-description')}>
                      <th>Beschreibung</th>
                      <td>{organizer.description}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Accordion.Collapse>
        </Accordion>
      )}
    </>
  )
}
